/* global FNGPlayer */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import scriptjs from 'scriptjs';
import _ from 'lodash';
import { isEpgv2Enabled } from 'helpers/ConfigHelper';
import Style from './VideoPlayer.module.scss';

const LIVE = 'live';

export default class VideoPlayer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      player: null,
    };
    this.load = true;
    this.loadPlayer = this.loadPlayer.bind(this);
  }

  componentDidMount() {
    const { requiresAuth, requiresAuthLive, token, mode, requestSignIn, releaseURL } = this.props;
    const vodRequiresAuth = mode !== LIVE && requiresAuth;
    const liveRequiresAuth = mode === LIVE && requiresAuthLive;
    const gated = vodRequiresAuth || liveRequiresAuth || !releaseURL;

    if (!requestSignIn && ((gated && token) || !gated)) {
      const defaultOptions = this.getDefaultOptions(this.props);
      this.loadPlayer(defaultOptions);
    }
  }

  shouldComponentUpdate(nextProps) {
    const {
      releaseURL,
      videoId,
      mode,
      token,
      playerReference,
      profileId,
      profileToken,
      requestSignIn,
      showResumeCTA,
      isFullscreen,
      playerType,
    } = this.props;

    const {
      releaseURL: nextReleaseURL,
      videoId: nextVideoId,
      mode: nextMode,
      token: nextToken,
      requiresAuth: requiresAuthNext,
      requiresAuthLive: requiresAuthLiveNext,
      profileId: nextProfileId,
      profileToken: nextProfileToken,
      requestSignIn: nextRequestSignIn,
      showResumeCTA: nextShowResumeCTA,
      isFullscreen: nextIsFullscreen,
      playerType: nextPlayerType,
    } = nextProps;
    const { player: playerState } = this.state;
    const player = playerState && window.player && window.player[playerReference];
    const errorOccurred = player?.errorOccurred;
    const accessTokenChange = profileToken !== nextProfileToken;
    const vodRequiresAuth = nextMode !== LIVE && requiresAuthNext;
    const liveRequiresAuth = nextMode === LIVE && requiresAuthLiveNext;
    const gated = vodRequiresAuth || liveRequiresAuth;
    const needsAuth = !nextReleaseURL || errorOccurred;
    const defaultOptions = this.getDefaultOptions(nextProps);
    if (accessTokenChange) {
      if (window.player && player && window?.player?.global?.options?.auth?.profile)
        window.player.global.options.auth.profile.accessToken = nextProfileToken;
    }
    if (needsAuth || nextRequestSignIn || nextShowResumeCTA) {
      this.unloadPlayer();
    } else if (!nextRequestSignIn && requestSignIn) {
      this.loadPlayer(defaultOptions);
    } else if (!nextShowResumeCTA && showResumeCTA) {
      this.loadPlayer(defaultOptions);
    } else if (token !== nextToken) {
      // eslint-disable-next-line no-unused-expressions
      player ? this.loadNextVideo(defaultOptions) : this.loadPlayer(defaultOptions);
    } else if (mode !== nextMode) {
      this.unloadPlayer();
      this.loadPlayer(defaultOptions);
    } else if (nextReleaseURL && nextReleaseURL !== releaseURL) {
      // eslint-disable-next-line no-unused-expressions
      player ? this.loadNextVideo(defaultOptions) : this.loadPlayer(defaultOptions);
    } else if (nextVideoId && nextVideoId !== videoId && mode !== LIVE && !gated) {
      const options = { ...defaultOptions, url: undefined };
      // eslint-disable-next-line no-unused-expressions
      player ? this.loadNextVideo(options) : this.loadPlayer(options);
    } else if (nextProfileId && nextProfileId !== profileId) {
      const options = { ...defaultOptions, url: undefined, id: undefined };
      player.setAuth(this.formatAuth(options));
    }

    if (isFullscreen !== nextIsFullscreen) return true;
    if (playerType !== nextPlayerType) return true;
    return false;
  }

  componentWillUnmount() {
    const { player } = this.state;
    if (player) this.unloadPlayer();
    this.load = false;
  }

  getDefaultOptions = passedProps => {
    const { hbaStatus, geolocation, mode, type } = this.props;
    const {
      releaseURL,
      videoId,
      liveContentId,
      token,
      profileAnonymous,
      profileId,
      profileToken,
      upstreamUserID,
      mvpdId,
      playerLoadingImage,
      playerConfig,
    } = passedProps;
    const { percentWatched, mediaInfo, continuous } = playerConfig;
    const zipcode = _.get(playerConfig, 'params.zipcode', null);
    const authorizingNetwork = _.get(playerConfig, 'authorizingNetwork');
    const networkEntitlementList = _.get(playerConfig, 'networkEntitlementList');
    return {
      authorizingNetwork,
      background: playerLoadingImage,
      continuous,
      geolocation,
      hbaStatus,
      id: videoId,
      liveContentId,
      mediaInfo,
      mode,
      mvpdId,
      networkEntitlementList,
      percentWatched,
      profileAnonymous,
      profileId,
      profileToken,
      token,
      type,
      upstreamUserID,
      url: releaseURL,
      zipcode,
    };
  };

  unloadPlayer = () => {
    const { playerReference, portalRef } = this.props;
    const { player } = this.state;
    if (player && player.remove && !portalRef) {
      player.remove();
      _.set(window, 'utag_data.collection_title', null);
    }
    if (window.player) window.player[playerReference] = null;
  };

  formatAuth = props => {
    const {
      geolocation,
      hbaStatus,
      jwt,
      token,
      profileAnonymous,
      profileId,
      profileToken,
      upstreamUserID,
      mvpdId,
      authorizingNetwork,
      networkEntitlementList,
      lastAnonymousProfileId,
      lastKnownProfileId,
    } = props;
    const auth = {};
    if (token) auth.authToken = token;
    if (jwt) auth['x-d2c-jwt'] = jwt;
    if (upstreamUserID) auth.upstreamUserID = upstreamUserID;
    if (!_.isNil(hbaStatus)) auth.hbaStatus = hbaStatus;
    if (mvpdId) auth.mvpdId = mvpdId;

    if (profileId) {
      auth.profile = {
        profileAnonymous,
        profileId,
        accessToken: profileToken,
        lastKnownProfileId,
        lastAnonymousProfileId,
      };
    } else if (profileToken) {
      auth.profile = {
        profileId: null,
        profileAnonymous,
        accessToken: profileToken,
      };
    }
    if (authorizingNetwork) auth.authorizingNetwork = authorizingNetwork;
    if (networkEntitlementList) auth.networkEntitlementList = networkEntitlementList;
    if (geolocation) {
      auth.geo = {
        geoLatitude: geolocation.latitude,
        geoLongitude: geolocation.longitude,
      };
    }

    return auth;
  };

  loadNextVideo = params => {
    const { playerReference } = this.props;
    const {
      abTest,
      background,
      continuous,
      id,
      liveContentId,
      mediaInfo,
      percentWatched,
      mode,
      url,
      zipcode,
    } = params;

    const videoId = mode === LIVE ? liveContentId : id;
    const player = _.get(window, `player.${playerReference}`, {});
    if (_.get(player, 'options')) delete window.player[playerReference].options.releaseURL;
    if (player.setConfig)
      player.setConfig({
        image: background,
        percentWatched,
        videoID: videoId,
        params: { abTest, zipcode },
        mediaInfo,
        continuous,
      });
    if (player.setAuth) player.setAuth(this.formatAuth(params));

    if (url) {
      player.playRelease(url);
    } else if (videoId) {
      player.playRelease(videoId);
    }
  };

  loadPlayer = opts => {
    const {
      hbaStatus,
      url,
      id,
      jwt,
      token: newToken,
      profileId,
      profileToken,
      scrubbingEnabled,
      upstreamUserID,
      liveContentId,
      percentWatched,
    } = opts;
    const {
      playerJs,
      releaseURL,
      videoId,
      type,
      playerConfig: { mediaInfo, ...propsPlayerConfig },
      loadPlayerCb,
      geolocation,
      profileAnonymous: defaultProfileAnonymous,
      profileId: defaultProfileId,
      profileToken: defaultProfileToken,
      upstreamUserID: defaultUpstreamUserID,
      scrubbingEnabled: defaultScrubbingEnabled,
      token,
      playerLoadingImage: background,
      tveEnabled,
      mode,
      lastAnonymousProfileId,
      lastKnownProfileId,
      playerReference,
      hbaStatus: defaultHbaStatus,
    } = this.props;
    const requiredMvpdAuth = _.get(mediaInfo, 'requiresMVPDAuth', true);
    const mvpdId = requiredMvpdAuth || null;

    const playerConfigOverrides = {};
    if (typeof percentWatched !== 'undefined')
      playerConfigOverrides.percentWatched = percentWatched;

    const loadURL = url || releaseURL;
    const loadId = id || videoId;
    const loadJwt = jwt;
    const loadToken = newToken || token;
    const newProfileAnonymous = _.get(opts, 'profileAnonymous', defaultProfileAnonymous);
    const newProfileId = profileId || defaultProfileId;
    const newProfileToken = profileToken || defaultProfileToken;
    const newUpstreamUserID = upstreamUserID || defaultUpstreamUserID;
    const newScrubbingEnabled = scrubbingEnabled || defaultScrubbingEnabled;
    const newHbaStatus = hbaStatus || defaultHbaStatus;
    const { authorizingNetwork } = propsPlayerConfig;

    if ((loadURL || loadId) && playerJs) {
      const configProps = {
        type,
        releaseURL: loadURL,
        videoId: loadId,
        liveContentId,
        jwt: loadJwt,
        token: loadToken,
        profileAnonymous: newProfileAnonymous,
        profileId: newProfileId,
        profileToken: newProfileToken,
        restartId: videoId,
        upstreamUserID: newUpstreamUserID,
        scrubbing: newScrubbingEnabled,
        mvpdId,
        background,
        authorizingNetwork,
        networkEntitlementList: ['foxnation'],
        tveEnabled,
        mode,
        geolocation,
        lastAnonymousProfileId,
        lastKnownProfileId,
        hbaStatus: newHbaStatus,
      };

      const overrides = {
        ...propsPlayerConfig,
        ...playerConfigOverrides,
      };

      const config = {
        ...this.getConfig(configProps),
        ...overrides,
      };

      // Downloading player JS
      scriptjs(playerJs, 'player');

      scriptjs.ready('player', () => {
        if (!this.load) return;
        const player = FNGPlayer.default;
        player.configPlayer(() => {
          player.load(config, player => {
            // TODO: Add events. Old events are currently on master
            // We will want to reimplement seeking
            // window.player = loadedPlayer;
            if (!window.player) window.player = {};
            window.player[playerReference] = player;
            this.setState({ player }, () => {
              if (loadPlayerCb) loadPlayerCb(player);
            });
          });
        });
      });
    }
  };

  getConfig = params => {
    const {
      background,
      liveContentId,
      mode,
      releaseURL,
      restartId,
      tveEnabled,
      type,
      videoId,
    } = params;

    const streamId = mode === 'restart' ? restartId : videoId;
    const streamType = mode === 'restart' ? 'liveRestart' : mode;
    const auth = this.formatAuth(params);
    const config = {
      id: this.getPlayerId(),
      width: '100%',
      height: '100%',
      auth,
      image: background,
      releaseURL,
      tveEnabled,
      watch: {
        streamId,
        streamType,
      },
    };

    if (mode === LIVE) {
      config.videoID = liveContentId;
    } else {
      config.videoID = videoId;
    }
    config.mode = mode;
    config.type = type;

    return config;
  };

  getPlayerId = () => {
    const { playerReference } = this.props;
    return `video-player-${playerReference}`;
  };

  render() {
    const { isFullscreen, playerType } = this.props;
    const classes = classNames(
      Style.container,
      isFullscreen ? Style.fullscreen : '',
      playerType !== 'standard' ? Style.heroContainer : '',
      isEpgv2Enabled() ? Style.resizablePlayer : null,
    );
    const playerId = this.getPlayerId();
    return (
      <div className={classes} key={playerId}>
        <div className={classNames(Style.videoContainer, Style[playerType])}>
          <div id={playerId} />
        </div>
      </div>
    );
  }
}

VideoPlayer.defaultProps = {
  geolocation: {},
  hbaStatus: null,
  isFullscreen: null,
  lastAnonymousProfileId: null,
  lastKnownProfileId: null,
  loadPlayerCb: () => {},
  mediaInfo: {},
  mode: null,
  playerConfig: {},
  playerLoadingImage: null,
  playerType: '',
  portalRef: '',
  profileAnonymous: null,
  profileId: null,
  profileToken: null,
  releaseURL: null,
  requestSignIn: null,
  requiresAuth: null,
  requiresAuthLive: null,
  scrubbingEnabled: null,
  showResumeCTA: null,
  token: null,
  tveEnabled: null,
  type: null,
  upstreamUserID: null,
  videoId: null,
};

VideoPlayer.propTypes = {
  geolocation: PropTypes.shape({}),
  hbaStatus: PropTypes.bool,
  isFullscreen: PropTypes.bool,
  lastAnonymousProfileId: PropTypes.string,
  lastKnownProfileId: PropTypes.string,
  loadPlayerCb: PropTypes.func,
  mediaInfo: PropTypes.shape({}),
  mode: PropTypes.string,
  playerConfig: PropTypes.shape({}),
  playerJs: PropTypes.string,
  playerLoadingImage: PropTypes.string,
  playerReference: PropTypes.string,
  playerType: PropTypes.string,
  portalRef: PropTypes.string,
  profileAnonymous: PropTypes.bool,
  profileId: PropTypes.string,
  profileToken: PropTypes.string,
  releaseURL: PropTypes.string,
  requestSignIn: PropTypes.bool,
  requiresAuth: PropTypes.bool,
  requiresAuthLive: PropTypes.bool,
  scrubbingEnabled: PropTypes.bool,
  showResumeCTA: PropTypes.bool,
  token: PropTypes.string,
  tveEnabled: PropTypes.bool,
  type: PropTypes.string,
  upstreamUserID: PropTypes.string,
  videoId: PropTypes.string,
};
